import React, { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { getDeals } from "../mock";
import { chatCompletion } from "../services/ai";
import { Plus, Trash2, Sparkles, CheckCircle2, AlertTriangle, Clock, Flag, FileText, MessageSquare, Calculator, Scale, ClipboardCheck } from "lucide-react";

// Hook for team changes
function useTeamChange() {
  const [teamKey, setTeamKey] = useState(0);
  useEffect(() => {
    const handler = () => setTeamKey(k => k + 1);
    window.addEventListener("atlas_team_changed", handler);
    return () => window.removeEventListener("atlas_team_changed", handler);
  }, []);
  return teamKey;
}

// ─── Storage helpers ───────────────────────────────────────────────────────────
const store = (key, val) => localStorage.setItem(key, JSON.stringify(val));
const load = (key, def = []) => { try { return JSON.parse(localStorage.getItem(key) || JSON.stringify(def)); } catch { return def; } };

// Storage keys
const CHECKLIST_KEY = "atlas_diligence_checklists";
const KPI_KEY = "atlas_diligence_kpis";
const RISK_KEY = "atlas_diligence_risks";
const BM_KEY = "atlas_business_models";
const MEMO_KEY = "atlas_underwriting_memos";

// ─── Constants ────────────────────────────────────────────────────────────────
const DILIGENCE_TYPES = ["Financial", "Operational"];
const CHECKLIST_STATUSES = ["Pending", "In Progress", "Complete", "Flagged"];
const PRIORITIES = ["High", "Medium", "Low"];
const RISK_TYPES = ["Financial", "Operational", "Legal", "Market", "Management"];
const SEVERITIES = ["High", "Medium", "Low"];
const RISK_STATUSES = ["Open", "Mitigated", "Accepted"];
const FLAG_COLORS = { Green: "text-emerald-600 bg-emerald-50 border-emerald-200", Yellow: "text-yellow-700 bg-yellow-50 border-yellow-200", Red: "text-red-600 bg-red-50 border-red-200" };
const STATUS_ICONS = { Pending: <Clock size={14} className="text-gray-400" />, "In Progress": <Clock size={14} className="text-blue-500" />, Complete: <CheckCircle2 size={14} className="text-emerald-500" />, Flagged: <AlertTriangle size={14} className="text-red-500" /> };
const PRIORITY_COLORS = { High: "text-red-600 bg-red-50 border-red-200", Medium: "text-yellow-700 bg-yellow-50 border-yellow-200", Low: "text-gray-600 bg-gray-50 border-gray-200" };
const APPROVAL_STATUSES = ["Draft", "Under Review", "Approved", "Rejected"];
const APPROVAL_COLORS = {
  Draft: "bg-gray-100 text-gray-600 border-gray-200",
  "Under Review": "bg-yellow-50 text-yellow-700 border-yellow-200",
  Approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Rejected: "bg-red-50 text-red-700 border-red-200",
};

const DEFAULT_FINANCIAL_ITEMS = [
  "Review 3 years of audited financial statements",
  "Validate revenue recognition policies",
  "Analyze EBITDA bridge and adjustments",
  "Review working capital and cash conversion cycle",
  "Assess debt structure and covenants",
  "Validate customer concentration and churn",
];
const DEFAULT_OPERATIONAL_ITEMS = [
  "Management team background checks",
  "Review key contracts and customer agreements",
  "Assess operational infrastructure and scalability",
  "Evaluate technology stack and IP ownership",
  "Review HR structure and retention risk",
  "Assess regulatory compliance and licensing",
];

// ─── Valuation Tab (LBO + DCF) ─────────────────────────────────────────────────
function ValuationTab() {
  const teamKey = useTeamChange();
  const deals = useMemo(() => getDeals(), [teamKey]);
  const [lbo, setLbo] = useState({ entryEV: 500, ebitda: 50, debtPct: 60, interest: 8, entryMultiple: 10, exitMultiple: 11, years: 5, taxRate: 25, fcfPct: 60 });
  const [lboRes, setLboRes] = useState({ irr: 0, moic: 0, exitEV: 0, exitEquity: 0, debtRemaining: 0 });
  const [dcf, setDcf] = useState({ revenue: 300, ebitdaMargin: 25, taxRate: 25, capexPct: 5, nwcPct: 2, wacc: 10, growth: 8, terminalGrowth: 3, years: 5 });
  const [dcfRes, setDcfRes] = useState({ ev: 0 });
  const [busy, setBusy] = useState(false);
  const [talk, setTalk] = useState("");
  const [dealCase, setDealCase] = useState("");
  const [chosen, setChosen] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filteredDeals = useMemo(() => {
    const q = search.toLowerCase();
    return deals.filter(d => !q || d.name.toLowerCase().includes(q) || d.sector.toLowerCase().includes(q));
  }, [deals, search]);

  // LBO calc
  useEffect(() => {
    const entryEquity = lbo.entryEV * (1 - lbo.debtPct / 100);
    const debt = lbo.entryEV * (lbo.debtPct / 100);
    const annualInterest = debt * (lbo.interest / 100);
    const annualFCF = lbo.ebitda * (lbo.fcfPct / 100) * (1 - lbo.taxRate / 100) - annualInterest;
    const debtRemaining = Math.max(0, debt - annualFCF * lbo.years);
    const exitEV = lbo.ebitda * lbo.exitMultiple;
    const exitEquity = Math.max(0, exitEV - debtRemaining);
    const moic = entryEquity > 0 ? +(exitEquity / entryEquity).toFixed(2) : 0;
    const irr = entryEquity > 0 ? +((Math.pow(moic, 1 / lbo.years) - 1) * 100).toFixed(1) : 0;
    setLboRes({ irr, moic, exitEV: Math.round(exitEV), exitEquity: Math.round(exitEquity), debtRemaining: Math.round(debtRemaining) });
  }, [lbo]);

  // DCF calc
  useEffect(() => {
    let pv = 0;
    let rev = dcf.revenue;
    for (let y = 1; y <= dcf.years; y++) {
      rev *= (1 + dcf.growth / 100);
      const fcf = rev * (dcf.ebitdaMargin / 100) * (1 - dcf.taxRate / 100) - rev * (dcf.capexPct / 100) - rev * (dcf.nwcPct / 100);
      pv += fcf / Math.pow(1 + dcf.wacc / 100, y);
    }
    const terminalFCF = rev * (dcf.ebitdaMargin / 100) * (1 - dcf.taxRate / 100) * (1 + dcf.terminalGrowth / 100);
    const terminalValue = terminalFCF / Math.max(0.001, (dcf.wacc - dcf.terminalGrowth) / 100);
    const pvTerminal = terminalValue / Math.pow(1 + dcf.wacc / 100, dcf.years);
    setDcfRes({ ev: Math.round(pv + pvTerminal) });
  }, [dcf]);

  const buildCase = async () => {
    setBusy(true); setTalk(""); setDealCase("");
    try {
      const ctx = chosen ? `Company: ${chosen.name}, Sector: ${chosen.sector}, EV: $${chosen.enterpriseValue}M, Score: ${chosen.score}` : "No specific deal selected";
      const prompt = `${ctx}\nLBO Results: IRR ${lboRes.irr}%, MOIC ${lboRes.moic}x, Exit EV $${lboRes.exitEV}M\nDCF EV: $${dcfRes.ev}M\nGenerate: 1) 3 IC talking points, 2) Deal case summary (3-4 sentences), 3) Key risks and mitigants.`;
      const { content } = await chatCompletion(prompt, { systemPrompt: "You are a PE deal professional at GII. Be concise, structured, and investment-committee ready." });
      const parts = (content || "").split(/\n\n/);
      setTalk(parts[0] || content);
      setDealCase(parts.slice(1).join("\n\n") || "");
    } catch (e) { setTalk(`Error: ${e.message}`); }
    setBusy(false);
  };

  function NumInput({ label, suffix, value, onChange, step = 1 }) {
    return (
      <div>
        <div className="text-xs font-medium text-atlas-description uppercase tracking-wide mb-1">{label}{suffix && <span className="ml-1 normal-case text-atlas-description">({suffix})</span>}</div>
        <div className="flex items-center border border-gray-200 rounded-md overflow-hidden bg-white">
          <input type="number" value={value} step={step} onChange={e => onChange(Number(e.target.value))} className="flex-1 px-3 py-2 text-sm text-atlas-heading bg-transparent outline-none" />
          {suffix && <span className="px-2 text-xs text-atlas-description bg-gray-50 border-l border-gray-200 py-2">{suffix}</span>}
        </div>
      </div>
    );
  }

  function ResultCard({ label, value }) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
        <div className="text-xs text-atlas-description uppercase tracking-wide">{label}</div>
        <div className="text-lg font-bold text-atlas-heading mt-0.5">{value}</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Deal Picker */}
      <Card className="border border-gray-100 shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm font-medium text-atlas-heading">Linked Deal:</span>
            <Button variant="outline" className="text-sm border-gray-200" onClick={() => setPickerOpen(true)}>
              {chosen ? chosen.name : "Select a deal…"}
            </Button>
            {chosen && <Badge className="text-xs bg-blue-50 text-atlas-accent border-blue-200">{chosen.sector}</Badge>}
          </div>
        </CardContent>
      </Card>

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle className="text-atlas-heading">Select Deal</DialogTitle></DialogHeader>
          <Input placeholder="Search deals…" value={search} onChange={e => setSearch(e.target.value)} className="mb-3 border-gray-200 text-sm" />
          <div className="max-h-64 overflow-y-auto space-y-1">
            {filteredDeals.map(d => (
              <button key={d.id} onClick={() => { setChosen(d); setPickerOpen(false); }} className="w-full text-left px-3 py-2 rounded hover:bg-gray-50 text-sm text-atlas-heading">
                <div className="font-medium">{d.name}</div>
                <div className="text-xs text-atlas-description">{d.sector} • EV ${d.enterpriseValue}M</div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* LBO Model */}
        <Card className="border border-gray-100 shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-atlas-heading">LBO Model</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <NumInput label="Entry EV" suffix="$M" value={lbo.entryEV} onChange={v => setLbo(p => ({ ...p, entryEV: v }))} />
              <NumInput label="EBITDA" suffix="$M" value={lbo.ebitda} onChange={v => setLbo(p => ({ ...p, ebitda: v }))} />
              <NumInput label="Debt %" suffix="%" value={lbo.debtPct} onChange={v => setLbo(p => ({ ...p, debtPct: v }))} />
              <NumInput label="Interest Rate" suffix="%" value={lbo.interest} onChange={v => setLbo(p => ({ ...p, interest: v }))} step={0.5} />
              <NumInput label="Exit Multiple" suffix="x" value={lbo.exitMultiple} onChange={v => setLbo(p => ({ ...p, exitMultiple: v }))} step={0.5} />
              <NumInput label="Hold Period" suffix="yrs" value={lbo.years} onChange={v => setLbo(p => ({ ...p, years: v }))} />
            </div>
            <div className="grid grid-cols-3 gap-2 pt-2 border-t border-gray-100">
              <ResultCard label="IRR" value={`${lboRes.irr}%`} />
              <ResultCard label="MOIC" value={`${lboRes.moic}x`} />
              <ResultCard label="Exit Equity" value={`$${lboRes.exitEquity}M`} />
            </div>
          </CardContent>
        </Card>

        {/* DCF Model */}
        <Card className="border border-gray-100 shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-atlas-heading">DCF Model</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <NumInput label="Revenue" suffix="$M" value={dcf.revenue} onChange={v => setDcf(p => ({ ...p, revenue: v }))} />
              <NumInput label="EBITDA Margin" suffix="%" value={dcf.ebitdaMargin} onChange={v => setDcf(p => ({ ...p, ebitdaMargin: v }))} />
              <NumInput label="WACC" suffix="%" value={dcf.wacc} onChange={v => setDcf(p => ({ ...p, wacc: v }))} step={0.5} />
              <NumInput label="Growth Rate" suffix="%" value={dcf.growth} onChange={v => setDcf(p => ({ ...p, growth: v }))} />
              <NumInput label="Terminal Growth" suffix="%" value={dcf.terminalGrowth} onChange={v => setDcf(p => ({ ...p, terminalGrowth: v }))} step={0.5} />
              <NumInput label="Forecast Years" suffix="yrs" value={dcf.years} onChange={v => setDcf(p => ({ ...p, years: v }))} />
            </div>
            <div className="pt-2 border-t border-gray-100">
              <ResultCard label="Enterprise Value" value={`$${dcfRes.ev}M`} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* AI Deal Case */}
      <Card className="border border-gray-100 shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-atlas-heading">AI IC Materials</span>
            <Button onClick={buildCase} disabled={busy} className="bg-atlas-accent hover:bg-blue-700 text-white text-sm">
              <Sparkles size={14} className="mr-1" />{busy ? "Generating…" : "Generate Deal Case"}
            </Button>
          </div>
          {(talk || dealCase) && (
            <div className="space-y-3 text-sm text-atlas-heading whitespace-pre-wrap bg-blue-50 border border-blue-100 rounded p-4">
              {talk && <div><strong>IC Talking Points:</strong><br />{talk}</div>}
              {dealCase && <div>{dealCase}</div>}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Diligence Tab (Checklists, KPIs, Risks) ──────────────────────────────────
function DiligenceTab() {
  const teamKey = useTeamChange();
  const deals = useMemo(() => getDeals(), [teamKey]);
  const [selectedDeal, setSelectedDeal] = useState(deals[0] || null);
  const [dealSearch, setDealSearch] = useState("");
  const [subTab, setSubTab] = useState("checklist");

  useEffect(() => {
    if (deals.length > 0 && !deals.find(d => d.id === selectedDeal?.id)) {
      setSelectedDeal(deals[0]);
    }
  }, [deals, selectedDeal?.id]);

  // Checklists
  const [checklists, setChecklists] = useState(() => load(CHECKLIST_KEY, []));
  const [newItem, setNewItem] = useState({ item: "", type: "Financial", priority: "Medium", assignee: "", due_date: "" });

  // KPIs
  const [kpis, setKpis] = useState(() => load(KPI_KEY, []));
  const [newKpi, setNewKpi] = useState({ metric: "", value: "", unit: "", flag: "Green", notes: "" });

  // Risks
  const [risks, setRisks] = useState(() => load(RISK_KEY, []));
  const [newRisk, setNewRisk] = useState({ title: "", type: "Financial", severity: "Medium", mitigation: "" });

  // AI
  const [aiBusy, setAiBusy] = useState(false);
  const [aiOutput, setAiOutput] = useState("");

  useEffect(() => { store(CHECKLIST_KEY, checklists); }, [checklists]);
  useEffect(() => { store(KPI_KEY, kpis); }, [kpis]);
  useEffect(() => { store(RISK_KEY, risks); }, [risks]);

  const did = selectedDeal?.id || "";
  const dealChecklists = checklists.filter(c => c.deal_id === did);
  const dealKpis = kpis.filter(k => k.deal_id === did);
  const dealRisks = risks.filter(r => r.deal_id === did);

  const filteredDeals = useMemo(() => {
    const q = dealSearch.toLowerCase();
    return deals.filter(d => !q || d.name.toLowerCase().includes(q) || d.sector.toLowerCase().includes(q));
  }, [deals, dealSearch]);

  // Initialize default checklists
  const initDefaults = (type) => {
    if (!did) return;
    const items = type === "Financial" ? DEFAULT_FINANCIAL_ITEMS : DEFAULT_OPERATIONAL_ITEMS;
    const newItems = items.map(item => ({ id: crypto.randomUUID(), deal_id: did, item, type, status: "Pending", priority: "Medium", assignee: "", due_date: "", created_at: Date.now() }));
    setChecklists(prev => [...prev, ...newItems]);
  };

  const addItem = () => {
    if (!newItem.item.trim() || !did) return;
    const obj = { id: crypto.randomUUID(), deal_id: did, ...newItem, status: "Pending", created_at: Date.now() };
    setChecklists(prev => [...prev, obj]);
    setNewItem({ item: "", type: "Financial", priority: "Medium", assignee: "", due_date: "" });
  };
  const updateItemStatus = (id, status) => setChecklists(prev => prev.map(c => c.id === id ? { ...c, status } : c));
  const deleteItem = (id) => setChecklists(prev => prev.filter(c => c.id !== id));

  const addKpi = () => {
    if (!newKpi.metric.trim() || !did) return;
    const obj = { id: crypto.randomUUID(), deal_id: did, ...newKpi, value: parseFloat(newKpi.value) || null, created_at: Date.now() };
    setKpis(prev => [...prev, obj]);
    setNewKpi({ metric: "", value: "", unit: "", flag: "Green", notes: "" });
  };
  const updateKpiFlag = (id, flag) => setKpis(prev => prev.map(k => k.id === id ? { ...k, flag } : k));
  const deleteKpi = (id) => setKpis(prev => prev.filter(k => k.id !== id));

  const addRisk = () => {
    if (!newRisk.title.trim() || !did) return;
    const obj = { id: crypto.randomUUID(), deal_id: did, ...newRisk, status: "Open", created_at: Date.now() };
    setRisks(prev => [...prev, obj]);
    setNewRisk({ title: "", type: "Financial", severity: "Medium", mitigation: "" });
  };
  const updateRiskStatus = (id, status) => setRisks(prev => prev.map(r => r.id === id ? { ...r, status } : r));
  const deleteRisk = (id) => setRisks(prev => prev.filter(r => r.id !== id));

  const runAI = async () => {
    if (!selectedDeal) return;
    setAiBusy(true);
    setAiOutput("");
    try {
      const checklistSummary = dealChecklists.map(c => `${c.item}: ${c.status}`).join("; ") || "No items";
      const kpiSummary = dealKpis.map(k => `${k.metric}: ${k.value}${k.unit || ""} (${k.flag})`).join("; ") || "No KPIs";
      const riskSummary = dealRisks.map(r => `${r.title} (${r.severity}): ${r.status}`).join("; ") || "No risks";
      const prompt = `Due diligence summary for ${selectedDeal.name} (sector: ${selectedDeal.sector}, EV: $${selectedDeal.enterpriseValue}M).\nChecklist: ${checklistSummary}\nKPIs: ${kpiSummary}\nRisks: ${riskSummary}\nProvide: 1) Overall diligence assessment, 2) Key findings, 3) Red flags, 4) Go/No-Go recommendation with rationale.`;
      const { content } = await chatCompletion(prompt, { systemPrompt: "You are a PE diligence expert. Be thorough but concise." });
      setAiOutput(content || "");
    } catch (e) {
      setAiOutput(`Error: ${e.message}`);
    } finally {
      setAiBusy(false);
    }
  };

  const checklistProgress = dealChecklists.length > 0 ? Math.round((dealChecklists.filter(c => c.status === "Complete").length / dealChecklists.length) * 100) : 0;

  return (
    <div className="grid lg:grid-cols-4 gap-6">
      {/* Deal Selector */}
      <div className="lg:col-span-1">
        <Card className="border border-gray-100 shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-atlas-heading">Select Deal</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <Input placeholder="Search deals…" value={dealSearch} onChange={e => setDealSearch(e.target.value)} className="text-sm border-gray-200" />
            <div className="max-h-64 overflow-y-auto space-y-1">
              {filteredDeals.map(d => (
                <button key={d.id} onClick={() => setSelectedDeal(d)} className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${selectedDeal?.id === d.id ? "bg-atlas-accent text-white" : "hover:bg-gray-50 text-atlas-heading"}`}>
                  <div className="font-medium truncate">{d.name}</div>
                  <div className={`text-xs ${selectedDeal?.id === d.id ? "text-blue-100" : "text-atlas-description"}`}>{d.sector} • {d.stage}</div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="lg:col-span-3 space-y-4">
        {!selectedDeal ? (
          <Card className="border border-gray-100 shadow-sm"><CardContent className="p-8 text-center text-atlas-description">Select a deal from the left to begin diligence.</CardContent></Card>
        ) : (
          <>
            <Card className="border border-gray-100 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-atlas-heading">{selectedDeal.name}</h2>
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      <Badge className="text-xs bg-gray-100 text-gray-600">{selectedDeal.sector}</Badge>
                      <span className="text-xs text-atlas-description">EV: <strong className="text-atlas-heading">${selectedDeal.enterpriseValue}M</strong></span>
                      <span className="text-xs text-atlas-description">Stage: <strong className="text-atlas-heading">{selectedDeal.stage}</strong></span>
                    </div>
                  </div>
                  <Button onClick={runAI} disabled={aiBusy} className="bg-atlas-accent hover:bg-blue-700 text-white text-sm">
                    <Sparkles size={14} className="mr-1" />{aiBusy ? "Analyzing…" : "AI Diligence Summary"}
                  </Button>
                </div>
                {dealChecklists.length > 0 && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-xs text-atlas-description mb-1">
                      <span>Checklist Progress</span>
                      <span className="font-semibold text-atlas-heading">{checklistProgress}%</span>
                    </div>
                    <Progress value={checklistProgress} className="h-2" />
                  </div>
                )}
              </CardContent>
            </Card>

            {aiOutput && (
              <Card className="border border-blue-100 bg-blue-50 shadow-sm">
                <CardContent className="p-4">
                  <div className="text-xs font-semibold text-atlas-accent mb-2 uppercase tracking-wide">AI Diligence Summary</div>
                  <div className="text-sm text-atlas-heading whitespace-pre-wrap">{aiOutput}</div>
                </CardContent>
              </Card>
            )}

            <Tabs value={subTab} onValueChange={setSubTab}>
              <TabsList className="bg-gray-100 border border-gray-200">
                <TabsTrigger value="checklist" className="text-sm data-[state=active]:bg-atlas-accent data-[state=active]:text-white">Checklist</TabsTrigger>
                <TabsTrigger value="kpis" className="text-sm data-[state=active]:bg-atlas-accent data-[state=active]:text-white">KPIs</TabsTrigger>
                <TabsTrigger value="risks" className="text-sm data-[state=active]:bg-atlas-accent data-[state=active]:text-white">Risks</TabsTrigger>
              </TabsList>

              <TabsContent value="checklist" className="space-y-3 mt-4">
                <div className="flex gap-2 mb-3">
                  <Button onClick={() => initDefaults("Financial")} variant="outline" className="text-xs border-gray-200">+ Financial Defaults</Button>
                  <Button onClick={() => initDefaults("Operational")} variant="outline" className="text-xs border-gray-200">+ Operational Defaults</Button>
                </div>
                <Card className="border border-gray-100 shadow-sm">
                  <CardContent className="p-3 space-y-2">
                    <div className="grid md:grid-cols-4 gap-2">
                      <Input placeholder="Checklist item" value={newItem.item} onChange={e => setNewItem(p => ({ ...p, item: e.target.value }))} className="text-sm border-gray-200 md:col-span-2" />
                      <select value={newItem.type} onChange={e => setNewItem(p => ({ ...p, type: e.target.value }))} className="text-sm border border-gray-200 rounded px-2 py-2 text-atlas-heading">
                        {DILIGENCE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <select value={newItem.priority} onChange={e => setNewItem(p => ({ ...p, priority: e.target.value }))} className="text-sm border border-gray-200 rounded px-2 py-2 text-atlas-heading">
                        {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                    <Button onClick={addItem} className="bg-atlas-accent hover:bg-blue-700 text-white text-sm"><Plus size={14} className="mr-1" /> Add Item</Button>
                  </CardContent>
                </Card>
                <div className="space-y-2">
                  {dealChecklists.length === 0 && <div className="text-sm text-atlas-description text-center py-6">No checklist items yet.</div>}
                  {dealChecklists.map(c => (
                    <div key={c.id} className="flex items-center gap-3 p-3 bg-white border border-gray-100 rounded-lg hover:border-gray-200">
                      {STATUS_ICONS[c.status]}
                      <div className="flex-1">
                        <div className="text-sm text-atlas-heading">{c.item}</div>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className={`text-xs border ${c.type === "Financial" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-purple-50 text-purple-700 border-purple-200"}`}>{c.type}</Badge>
                          <Badge className={`text-xs border ${PRIORITY_COLORS[c.priority]}`}>{c.priority}</Badge>
                        </div>
                      </div>
                      <select value={c.status} onChange={e => updateItemStatus(c.id, e.target.value)} className="text-xs border border-gray-200 rounded px-2 py-1 text-atlas-heading">
                        {CHECKLIST_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <button onClick={() => deleteItem(c.id)} className="text-red-400 hover:text-red-600"><Trash2 size={14} /></button>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="kpis" className="space-y-3 mt-4">
                <Card className="border border-gray-100 shadow-sm">
                  <CardContent className="p-3 space-y-2">
                    <div className="grid md:grid-cols-4 gap-2">
                      <Input placeholder="Metric" value={newKpi.metric} onChange={e => setNewKpi(p => ({ ...p, metric: e.target.value }))} className="text-sm border-gray-200" />
                      <Input placeholder="Value" type="number" value={newKpi.value} onChange={e => setNewKpi(p => ({ ...p, value: e.target.value }))} className="text-sm border-gray-200" />
                      <Input placeholder="Unit" value={newKpi.unit} onChange={e => setNewKpi(p => ({ ...p, unit: e.target.value }))} className="text-sm border-gray-200" />
                      <select value={newKpi.flag} onChange={e => setNewKpi(p => ({ ...p, flag: e.target.value }))} className="text-sm border border-gray-200 rounded px-2 py-2 text-atlas-heading">
                        {["Green", "Yellow", "Red"].map(f => <option key={f} value={f}>{f}</option>)}
                      </select>
                    </div>
                    <Button onClick={addKpi} className="bg-atlas-accent hover:bg-blue-700 text-white text-sm"><Plus size={14} className="mr-1" /> Add KPI</Button>
                  </CardContent>
                </Card>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="text-xs text-atlas-description">Metric</TableHead>
                        <TableHead className="text-xs text-atlas-description">Value</TableHead>
                        <TableHead className="text-xs text-atlas-description">Flag</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dealKpis.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-atlas-description text-sm py-6">No KPIs tracked yet.</TableCell></TableRow>}
                      {dealKpis.map(k => (
                        <TableRow key={k.id} className="hover:bg-gray-50">
                          <TableCell className="text-sm font-medium text-atlas-heading">{k.metric}</TableCell>
                          <TableCell className="text-sm text-atlas-heading">{k.value != null ? `${k.value}${k.unit || ""}` : "—"}</TableCell>
                          <TableCell>
                            <select value={k.flag} onChange={e => updateKpiFlag(k.id, e.target.value)} className={`text-xs border rounded px-2 py-1 ${FLAG_COLORS[k.flag]}`}>
                              {["Green", "Yellow", "Red"].map(f => <option key={f} value={f}>{f}</option>)}
                            </select>
                          </TableCell>
                          <TableCell><button onClick={() => deleteKpi(k.id)} className="text-red-400 hover:text-red-600"><Trash2 size={14} /></button></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>

              <TabsContent value="risks" className="space-y-3 mt-4">
                <Card className="border border-gray-100 shadow-sm">
                  <CardContent className="p-3 space-y-2">
                    <div className="grid md:grid-cols-3 gap-2">
                      <Input placeholder="Risk title" value={newRisk.title} onChange={e => setNewRisk(p => ({ ...p, title: e.target.value }))} className="text-sm border-gray-200" />
                      <select value={newRisk.type} onChange={e => setNewRisk(p => ({ ...p, type: e.target.value }))} className="text-sm border border-gray-200 rounded px-2 py-2 text-atlas-heading">
                        {RISK_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <select value={newRisk.severity} onChange={e => setNewRisk(p => ({ ...p, severity: e.target.value }))} className="text-sm border border-gray-200 rounded px-2 py-2 text-atlas-heading">
                        {SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <textarea placeholder="Mitigation plan…" value={newRisk.mitigation} onChange={e => setNewRisk(p => ({ ...p, mitigation: e.target.value }))} rows={2} className="w-full text-sm border border-gray-200 rounded px-3 py-2 text-atlas-heading resize-none" />
                    <Button onClick={addRisk} className="bg-atlas-accent hover:bg-blue-700 text-white text-sm"><Plus size={14} className="mr-1" /> Add Risk</Button>
                  </CardContent>
                </Card>
                <div className="space-y-2">
                  {dealRisks.length === 0 && <div className="text-sm text-atlas-description text-center py-6">No risks identified yet.</div>}
                  {dealRisks.map(r => (
                    <div key={r.id} className="p-4 bg-white border border-gray-100 rounded-lg hover:border-gray-200">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="text-sm font-semibold text-atlas-heading">{r.title}</span>
                            <Badge className="text-xs bg-gray-100 text-gray-600 border-gray-200">{r.type}</Badge>
                            <Badge className={`text-xs border ${PRIORITY_COLORS[r.severity]}`}>{r.severity}</Badge>
                          </div>
                          {r.mitigation && <div className="text-xs text-atlas-description">Mitigation: {r.mitigation}</div>}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <select value={r.status} onChange={e => updateRiskStatus(r.id, e.target.value)} className="text-xs border border-gray-200 rounded px-2 py-1 text-atlas-heading">
                            {RISK_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                          <button onClick={() => deleteRisk(r.id)} className="text-red-400 hover:text-red-600"><Trash2 size={14} /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Underwriting Tab (Business Models, Memos) ────────────────────────────────
function UnderwritingTab() {
  const teamKey = useTeamChange();
  const deals = useMemo(() => getDeals(), [teamKey]);
  const [selectedDeal, setSelectedDeal] = useState(deals[0] || null);
  const [dealSearch, setDealSearch] = useState("");
  const [subTab, setSubTab] = useState("models");

  useEffect(() => {
    if (deals.length > 0 && !deals.find(d => d.id === selectedDeal?.id)) {
      setSelectedDeal(deals[0]);
    }
  }, [deals, selectedDeal?.id]);

  // Business Models - ensure we always have an array
  const [models, setModels] = useState(() => {
    const data = load(BM_KEY, []);
    return Array.isArray(data) ? data : [];
  });
  const [newModel, setNewModel] = useState({ title: "", revenue_drivers: "", cost_structure: "", moat: "", key_assumptions: "" });

  // Memos - ensure we always have an array
  const [memos, setMemos] = useState(() => {
    const data = load(MEMO_KEY, []);
    return Array.isArray(data) ? data : [];
  });
  const [newMemo, setNewMemo] = useState({ title: "", content: "", author: "" });

  // AI
  const [aiBusy, setAiBusy] = useState(false);
  const [aiOutput, setAiOutput] = useState("");

  useEffect(() => { store(BM_KEY, models); }, [models]);
  useEffect(() => { store(MEMO_KEY, memos); }, [memos]);

  const did = selectedDeal?.id || "";
  const dealModels = Array.isArray(models) ? models.filter(m => m.deal_id === did) : [];
  const dealMemos = Array.isArray(memos) ? memos.filter(m => m.deal_id === did) : [];

  const filteredDeals = useMemo(() => {
    const q = dealSearch.toLowerCase();
    return deals.filter(d => !q || d.name.toLowerCase().includes(q) || d.sector.toLowerCase().includes(q));
  }, [deals, dealSearch]);

  const addModel = () => {
    if (!newModel.title.trim() || !did) return;
    const obj = { id: crypto.randomUUID(), deal_id: did, ...newModel, created_at: Date.now(), updated_at: Date.now() };
    setModels(prev => [obj, ...prev]);
    setNewModel({ title: "", revenue_drivers: "", cost_structure: "", moat: "", key_assumptions: "" });
  };
  const deleteModel = (id) => setModels(prev => prev.filter(m => m.id !== id));

  const addMemo = () => {
    if (!newMemo.title.trim() || !did) return;
    const obj = { id: crypto.randomUUID(), deal_id: did, ...newMemo, status: "Draft", created_at: Date.now(), updated_at: Date.now() };
    setMemos(prev => [obj, ...prev]);
    setNewMemo({ title: "", content: "", author: "" });
  };
  const updateMemoStatus = (id, status) => setMemos(prev => prev.map(m => m.id === id ? { ...m, status, updated_at: Date.now() } : m));
  const deleteMemo = (id) => setMemos(prev => prev.filter(m => m.id !== id));

  const runAI = async () => {
    if (!selectedDeal) return;
    setAiBusy(true);
    setAiOutput("");
    try {
      const modelSummary = dealModels.map(m => `${m.title}: Revenue=${m.revenue_drivers}, Moat=${m.moat}`).join("; ") || "No models";
      const prompt = `Underwriting summary for ${selectedDeal.name} (sector: ${selectedDeal.sector}, EV: $${selectedDeal.enterpriseValue}M).\nBusiness Models: ${modelSummary}\nProvide: 1) Business model assessment, 2) Competitive positioning, 3) Key investment thesis points, 4) Structuring considerations.`;
      const { content } = await chatCompletion(prompt, { systemPrompt: "You are a PE underwriting expert. Be thorough and IC-ready." });
      setAiOutput(content || "");
    } catch (e) {
      setAiOutput(`Error: ${e.message}`);
    } finally {
      setAiBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-4 gap-6">
      {/* Deal Selector */}
      <div className="lg:col-span-1">
        <Card className="border border-gray-100 shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-atlas-heading">Select Deal</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <Input placeholder="Search deals…" value={dealSearch} onChange={e => setDealSearch(e.target.value)} className="text-sm border-gray-200" />
            <div className="max-h-64 overflow-y-auto space-y-1">
              {filteredDeals.map(d => (
                <button key={d.id} onClick={() => setSelectedDeal(d)} className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${selectedDeal?.id === d.id ? "bg-atlas-accent text-white" : "hover:bg-gray-50 text-atlas-heading"}`}>
                  <div className="font-medium truncate">{d.name}</div>
                  <div className={`text-xs ${selectedDeal?.id === d.id ? "text-blue-100" : "text-atlas-description"}`}>{d.sector} • {d.stage}</div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="lg:col-span-3 space-y-4">
        {!selectedDeal ? (
          <Card className="border border-gray-100 shadow-sm"><CardContent className="p-8 text-center text-atlas-description">Select a deal from the left to begin underwriting.</CardContent></Card>
        ) : (
          <>
            <Card className="border border-gray-100 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-atlas-heading">{selectedDeal.name}</h2>
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      <Badge className="text-xs bg-gray-100 text-gray-600">{selectedDeal.sector}</Badge>
                      <span className="text-xs text-atlas-description">EV: <strong className="text-atlas-heading">${selectedDeal.enterpriseValue}M</strong></span>
                    </div>
                  </div>
                  <Button onClick={runAI} disabled={aiBusy} className="bg-atlas-accent hover:bg-blue-700 text-white text-sm">
                    <Sparkles size={14} className="mr-1" />{aiBusy ? "Analyzing…" : "AI Underwriting Brief"}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {aiOutput && (
              <Card className="border border-blue-100 bg-blue-50 shadow-sm">
                <CardContent className="p-4">
                  <div className="text-xs font-semibold text-atlas-accent mb-2 uppercase tracking-wide">AI Underwriting Brief</div>
                  <div className="text-sm text-atlas-heading whitespace-pre-wrap">{aiOutput}</div>
                </CardContent>
              </Card>
            )}

            <Tabs value={subTab} onValueChange={setSubTab}>
              <TabsList className="bg-gray-100 border border-gray-200">
                <TabsTrigger value="models" className="text-sm data-[state=active]:bg-atlas-accent data-[state=active]:text-white">Business Models</TabsTrigger>
                <TabsTrigger value="memos" className="text-sm data-[state=active]:bg-atlas-accent data-[state=active]:text-white">IC Memos</TabsTrigger>
              </TabsList>

              <TabsContent value="models" className="space-y-3 mt-4">
                <Card className="border border-gray-100 shadow-sm">
                  <CardContent className="p-3 space-y-2">
                    <Input placeholder="Model title (e.g. Base Case, Upside)" value={newModel.title} onChange={e => setNewModel(p => ({ ...p, title: e.target.value }))} className="text-sm border-gray-200" />
                    <div className="grid md:grid-cols-2 gap-2">
                      <textarea placeholder="Revenue drivers…" value={newModel.revenue_drivers} onChange={e => setNewModel(p => ({ ...p, revenue_drivers: e.target.value }))} rows={2} className="text-sm border border-gray-200 rounded px-3 py-2 text-atlas-heading resize-none" />
                      <textarea placeholder="Cost structure…" value={newModel.cost_structure} onChange={e => setNewModel(p => ({ ...p, cost_structure: e.target.value }))} rows={2} className="text-sm border border-gray-200 rounded px-3 py-2 text-atlas-heading resize-none" />
                    </div>
                    <textarea placeholder="Moat / competitive advantages…" value={newModel.moat} onChange={e => setNewModel(p => ({ ...p, moat: e.target.value }))} rows={2} className="w-full text-sm border border-gray-200 rounded px-3 py-2 text-atlas-heading resize-none" />
                    <Button onClick={addModel} className="bg-atlas-accent hover:bg-blue-700 text-white text-sm"><Plus size={14} className="mr-1" /> Add Model</Button>
                  </CardContent>
                </Card>
                <div className="space-y-3">
                  {dealModels.length === 0 && <div className="text-sm text-atlas-description text-center py-6">No business models yet.</div>}
                  {dealModels.map(m => (
                    <Card key={m.id} className="border border-gray-100 shadow-sm">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="text-sm font-semibold text-atlas-heading mb-2">{m.title}</div>
                            {m.revenue_drivers && <div className="text-xs text-atlas-description mb-1"><strong>Revenue:</strong> {m.revenue_drivers}</div>}
                            {m.cost_structure && <div className="text-xs text-atlas-description mb-1"><strong>Costs:</strong> {m.cost_structure}</div>}
                            {m.moat && <div className="text-xs text-atlas-description"><strong>Moat:</strong> {m.moat}</div>}
                          </div>
                          <button onClick={() => deleteModel(m.id)} className="text-red-400 hover:text-red-600"><Trash2 size={14} /></button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="memos" className="space-y-3 mt-4">
                <Card className="border border-gray-100 shadow-sm">
                  <CardContent className="p-3 space-y-2">
                    <div className="grid md:grid-cols-2 gap-2">
                      <Input placeholder="Memo title" value={newMemo.title} onChange={e => setNewMemo(p => ({ ...p, title: e.target.value }))} className="text-sm border-gray-200" />
                      <Input placeholder="Author" value={newMemo.author} onChange={e => setNewMemo(p => ({ ...p, author: e.target.value }))} className="text-sm border-gray-200" />
                    </div>
                    <textarea placeholder="Memo content…" value={newMemo.content} onChange={e => setNewMemo(p => ({ ...p, content: e.target.value }))} rows={4} className="w-full text-sm border border-gray-200 rounded px-3 py-2 text-atlas-heading resize-none" />
                    <Button onClick={addMemo} className="bg-atlas-accent hover:bg-blue-700 text-white text-sm"><Plus size={14} className="mr-1" /> Add Memo</Button>
                  </CardContent>
                </Card>
                <div className="space-y-3">
                  {dealMemos.length === 0 && <div className="text-sm text-atlas-description text-center py-6">No memos yet.</div>}
                  {dealMemos.map(m => (
                    <Card key={m.id} className="border border-gray-100 shadow-sm">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-sm font-semibold text-atlas-heading">{m.title}</span>
                              <Badge className={`text-xs border ${APPROVAL_COLORS[m.status]}`}>{m.status}</Badge>
                            </div>
                            {m.author && <div className="text-xs text-atlas-description mb-2">By: {m.author}</div>}
                            <div className="text-sm text-atlas-heading whitespace-pre-wrap">{m.content}</div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <select value={m.status} onChange={e => updateMemoStatus(m.id, e.target.value)} className="text-xs border border-gray-200 rounded px-2 py-1 text-atlas-heading">
                              {APPROVAL_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                            <button onClick={() => deleteMemo(m.id)} className="text-red-400 hover:text-red-600"><Trash2 size={14} /></button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function DueDiligence() {
  const [activeTab, setActiveTab] = useState("valuation");

  return (
    <div className="space-y-6 p-6 bg-white min-h-screen">
      <div>
        <h1 className="text-2xl font-bold text-atlas-heading">Due Diligence</h1>
        <p className="text-sm text-atlas-description mt-1">Valuation models, diligence checklists, and underwriting materials</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-100 border border-gray-200">
          <TabsTrigger value="valuation" className="text-sm data-[state=active]:bg-atlas-accent data-[state=active]:text-white">
            <Calculator size={14} className="mr-1" /> Valuation
          </TabsTrigger>
          <TabsTrigger value="diligence" className="text-sm data-[state=active]:bg-atlas-accent data-[state=active]:text-white">
            <ClipboardCheck size={14} className="mr-1" /> Diligence
          </TabsTrigger>
          <TabsTrigger value="underwriting" className="text-sm data-[state=active]:bg-atlas-accent data-[state=active]:text-white">
            <Scale size={14} className="mr-1" /> Underwriting
          </TabsTrigger>
        </TabsList>

        <TabsContent value="valuation" className="mt-4">
          <ValuationTab />
        </TabsContent>

        <TabsContent value="diligence" className="mt-4">
          <DiligenceTab />
        </TabsContent>

        <TabsContent value="underwriting" className="mt-4">
          <UnderwritingTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
